import { inject, injectable } from 'tsyringe';
import { NoRecommendationsReasons } from '../no-recommendations-reasons.enums';
import { PersistentStorageService } from '../../persistent-storage-service/persistent-storage.service';
import { ConditionContext } from './interfaces/condition-context.interface';
import { ICondition, MatchResult } from './interfaces/condition.interface';
import { RecommendationDatastoreService } from '../../datastore-service/recommendation-datastore.service';
import {
  conditionType,
  ConditionType,
} from './decorators/condition-type.decorator';
import { Application } from '../../../models/application.model';
import { INotificationDisplayDetails } from 'app-core/src/models/notification-display-details.model';
import {
  IUserActionService,
  IUserActionServiceDelegate,
  kNameOfUserActionService,
} from '../../../interfaces/user-action-service.interface';
import { Utils } from '../../../utils/utils';
import { LoggerService } from '../../logger.service';

// -----------------------------------------------------------------------------
@injectable()
@conditionType(ConditionType.AllApps)
export class ConditionNotificationPreviouslyDismissed
  implements ICondition, IUserActionServiceDelegate
{
  private readonly logger = LoggerService.getCategory(
    'ConditionNotificationPreviouslyDismissed',
  );

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly datastoreService: RecommendationDatastoreService,
    private readonly persistentLocalStorage: PersistentStorageService,
    @inject(kNameOfUserActionService)
    private readonly userActionService: IUserActionService,
  ) {
    this.userActionService.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // ICondition
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public get id(): string {
    return this.logger.name;
  }

  // ---------------------------------------------------------------------------
  async match(context: ConditionContext): Promise<MatchResult> {
    const { application: app } = context;

    const dismissedTimestamp = this.getApplicationDismissedTimestamp(app);
    if (!dismissedTimestamp) {
      return [true];
    }

    const shouldResetDismissal = await this.didPassDismissalResetTime(
      dismissedTimestamp,
    );

    if (shouldResetDismissal) {
      this.logger.info(`${app.name} (${app.uid}) resetting dismiss timestamp.`);
      this.persistentLocalStorage.removeNotificationFromDismissedApps(app);
      return [true];
    }

    this.logger.info(
      `${app.name} (${app.uid}) was dismissed by user - won't recommend.`,
    );
    return [false, NoRecommendationsReasons.DismissedByUser];
  }

  // ---------------------------------------------------------------------------
  private getApplicationDismissedTimestamp(app: Application): number {
    const dismissedApps =
      this.persistentLocalStorage.getDismissedNotifications();

    if (!dismissedApps?.length) {
      return 0;
    }

    const dismissedAppDetails = dismissedApps.find((dismissedApp) => {
      return dismissedApp.applicationId === app.uid;
    });

    const dismissedTimestamp = dismissedAppDetails?.timestamp;
    return dismissedTimestamp;
  }

  // ---------------------------------------------------------------------------
  private async didPassDismissalResetTime(
    dismissedTimestamp: number,
  ): Promise<boolean> {
    const configs = await this.datastoreService.getGlobalConfigurations();
    const resetTimeInWeeks = configs.resetNotificationDismissPeriodWeeks;

    const notificationDismissedInWeeks =
      Utils.calcTimePassedInWeeks(dismissedTimestamp);

    if (resetTimeInWeeks <= notificationDismissedInWeeks) {
      return true;
    }

    return false;
  }

  // ---------------------------------------------------------------------------
  // IUserActionServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onUserActionDismissed(activeNotification: INotificationDisplayDetails) {
    const { applicationUid, gameId } = activeNotification;
    this.logger.info(
      `The notification (${gameId}, ${applicationUid}) was Dismissed.`,
    );

    this.persistentLocalStorage.setNotificationDismissed(applicationUid);
  }
}
